import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  BuildingOfficeIcon,
  UserPlusIcon,
  KeyIcon,
  XMarkIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PlusCircleIcon,
  ArrowsRightLeftIcon
} from '@heroicons/react/24/outline';
import organizationService, {
  Organization,
  PlatformSafeguard,
  UpdatePlatformSafeguardRequest,
} from '../../services/organizationService';
import authService from '../../features/auth/services/authService';
import { usePreloadStore } from '../../store/preloadStore';
import DialpadSafeguardsPanel from '../settings/DialpadSafeguardsPanel';

interface OrganizationSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const OrganizationSettingsModal: React.FC<OrganizationSettingsModalProps> = ({ isOpen, onClose }) => {
  const { clearPreload } = usePreloadStore();
  const [activeSection, setActiveSection] = useState<'details' | 'dialpad-safeguards' | 'invite-email' | 'create-user' | 'create-org' | 'switch-org'>('details');
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [dialpadSafeguard, setDialpadSafeguard] = useState<PlatformSafeguard | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialpadSafeguardLoading, setDialpadSafeguardLoading] = useState(true);
  const [savingDialpadSafeguard, setSavingDialpadSafeguard] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [userInfo, setUserInfo] = useState<any>(null);

  // Invite user form (email only)
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviting, setInviting] = useState(false);

  // Create user form (email + temp password)
  const [createEmail, setCreateEmail] = useState('');
  const [createFirstName, setCreateFirstName] = useState('');
  const [createLastName, setCreateLastName] = useState('');
  const [tempPassword, setTempPassword] = useState('');
  const [creating, setCreating] = useState(false);

  // Create organization form
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgDescription, setNewOrgDescription] = useState('');
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [newOrgCode, setNewOrgCode] = useState<string | null>(null);

  // Switch organization
  const [allOrganizations, setAllOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadOrganization();
      loadDialpadSafeguard();
      loadUserInfo();
      loadAllOrganizations();
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const loadUserInfo = async () => {
    try {
      const info = await authService.getUserInfo();
      setUserInfo(info);
    } catch (err) {
      console.error('Failed to load user info:', err);
    }
  };

  const loadAllOrganizations = async () => {
    try {
      const orgs = await organizationService.listAllOrganizations();
      setAllOrganizations(orgs);
    } catch (err) {
      console.error('Failed to load organizations:', err);
    }
  };

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const loadOrganization = async () => {
    try {
      setLoading(true);
      const org = await organizationService.getCurrentOrganization();
      setOrganization(org);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load organization');
    } finally {
      setLoading(false);
    }
  };

  const loadDialpadSafeguard = async () => {
    try {
      setDialpadSafeguardLoading(true);
      const policy = await organizationService.getCurrentPlatformSafeguard('dialpad');
      setDialpadSafeguard(policy);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load Dialpad safeguards');
    } finally {
      setDialpadSafeguardLoading(false);
    }
  };

  const handleSaveDialpadSafeguard = async (data: UpdatePlatformSafeguardRequest) => {
    try {
      setSavingDialpadSafeguard(true);
      setError(null);
      const updated = await organizationService.updateCurrentPlatformSafeguard('dialpad', data);
      setDialpadSafeguard(updated);
      setSuccess('Dialpad safeguards saved successfully.');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save Dialpad safeguards');
    } finally {
      setSavingDialpadSafeguard(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleInviteUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviting(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await organizationService.inviteUser({
        email: inviteEmail,
        first_name: inviteFirstName,
        last_name: inviteLastName,
      });
      setSuccess(`Invitation sent to ${result.email}! They will receive an email with a temporary password.`);
      setInviteEmail('');
      setInviteFirstName('');
      setInviteLastName('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await organizationService.createUserWithPassword({
        email: createEmail,
        first_name: createFirstName,
        last_name: createLastName,
        temporary_password: tempPassword,
      });
      setSuccess(
        `User created successfully!\nEmail: ${result.email}\nTemporary Password: ${result.temporary_password}`
      );
      setCreateEmail('');
      setCreateFirstName('');
      setCreateLastName('');
      setTempPassword('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setTempPassword(password);
  };

  const handleCreateOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreatingOrg(true);
    setError(null);
    setSuccess(null);
    setNewOrgCode(null);

    try {
      const result = await organizationService.createOrganization({
        name: newOrgName,
        description: newOrgDescription,
      });
      setNewOrgCode(result.organization_code);
      setSuccess(`Organization "${result.name}" created successfully! Code: ${result.organization_code}`);
      setNewOrgName('');
      setNewOrgDescription('');
      loadAllOrganizations(); // Refresh org list
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create organization');
    } finally {
      setCreatingOrg(false);
    }
  };

  const handleSwitchOrganization = async () => {
    if (!selectedOrgId) {
      setError('Please select an organization');
      return;
    }

    setSwitching(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await organizationService.switchOrganization(selectedOrgId);
      setSuccess(result.message);
      // Clear preload cache to force fresh data on reload
      clearPreload();
      // Reload page to refresh all data
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to switch organization');
    } finally {
      setSwitching(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div 
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="relative bg-theme-bg dark:bg-dark-theme-bg rounded-lg shadow-2xl w-[95vw] h-[85vh] max-w-6xl overflow-hidden border border-theme-border dark:border-dark-theme-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col md:flex-row h-full">
          {/* Sidebar */}
          <div className="w-full md:w-64 bg-theme-bg dark:bg-dark-theme-bg md:rounded-l-lg p-4 md:p-6 flex-shrink-0 border-b md:border-b-0 md:border-r border-theme-border dark:border-dark-theme-border">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-theme-text dark:text-dark-theme-text">
                Organization
              </h2>
              <button
                onClick={onClose}
                className="text-theme-text-secondary dark:text-dark-theme-text-secondary hover:text-theme-text dark:hover:text-dark-theme-text"
                aria-label="Close organization settings"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <nav className="space-y-2">
              {[
                { id: 'details', label: 'Organization Details', icon: BuildingOfficeIcon },
                { id: 'dialpad-safeguards', label: 'Dialpad Safeguards', icon: ExclamationTriangleIcon },
                { id: 'invite-email', label: 'Invite User (Email)', icon: UserPlusIcon },
                { id: 'create-user', label: 'Create User (Password)', icon: KeyIcon },
                ...(userInfo?.user?.is_superuser ? [
                  { id: 'create-org', label: 'Create Organization', icon: PlusCircleIcon },
                  { id: 'switch-org', label: 'Switch Organization', icon: ArrowsRightLeftIcon }
                ] : [])
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveSection(tab.id as any)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-left transition-colors ${
                      activeSection === tab.id
                        ? 'bg-primary-light/10 dark:bg-primary-dark/10 text-primary-light dark:text-primary-dark'
                        : 'text-theme-text-secondary dark:text-dark-theme-text-secondary hover:bg-theme-bg-secondary dark:hover:bg-dark-theme-bg-secondary'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="font-medium text-sm md:text-base">{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 p-4 md:p-6 overflow-y-auto">
            {/* Success/Error Messages */}
            {success && (
              <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                  <p className="text-green-700 dark:text-green-300 whitespace-pre-wrap">{success}</p>
                </div>
              </div>
            )}

            {error && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2" />
                  <p className="text-red-700 dark:text-red-300">{error}</p>
                </div>
              </div>
            )}

            {/* Loading State */}
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-theme-text-secondary dark:text-dark-theme-text-secondary">
                  Loading organization...
                </div>
              </div>
            ) : (
              <>
                {/* Organization Details Tab */}
                {activeSection === 'details' && organization && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold text-theme-text dark:text-dark-theme-text mb-6">
                        Organization Details
                      </h3>
                    </div>

                    {/* Organization Info */}
                    <div className="bg-theme-bg dark:bg-dark-theme-bg rounded-lg p-6 border border-theme-border dark:border-dark-theme-border">
                      <h4 className="text-lg font-semibold text-theme-text dark:text-dark-theme-text mb-4">
                        Information
                      </h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary mb-1">
                            Name
                          </label>
                          <p className="text-theme-text dark:text-dark-theme-text">{organization.name}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary mb-1">
                            Description
                          </label>
                          <p className="text-theme-text dark:text-dark-theme-text">
                            {organization.description || 'No description'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Organization Code */}
                    <div className="bg-gradient-to-r from-primary-light/10 to-primary-dark/10 dark:from-primary-dark/20 dark:to-primary-light/20 border border-primary-light/20 dark:border-primary-dark/20 rounded-lg p-6">
                      <h4 className="text-lg font-semibold text-theme-text dark:text-dark-theme-text mb-2">
                        Organization Code
                      </h4>
                      <p className="text-sm text-theme-text-secondary dark:text-dark-theme-text-secondary mb-4">
                        Share this code with users to join your organization
                      </p>
                      <div className="flex items-center space-x-3">
                        <div className="flex-1 p-4 bg-white dark:bg-gray-800 border border-theme-border dark:border-dark-theme-border rounded-lg">
                          <code className="text-2xl font-mono font-bold text-primary-light dark:text-primary-dark">
                            {organization.organization_code}
                          </code>
                        </div>
                        <button
                          onClick={() => copyToClipboard(organization.organization_code)}
                          className="p-4 bg-primary-light dark:bg-primary-dark text-white rounded-lg hover:opacity-90 transition-opacity"
                          title="Copy organization code"
                        >
                          {copied ? (
                            <CheckIcon className="w-6 h-6" />
                          ) : (
                            <ClipboardDocumentIcon className="w-6 h-6" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === 'dialpad-safeguards' && (
                  <DialpadSafeguardsPanel
                    policy={dialpadSafeguard}
                    isLoading={dialpadSafeguardLoading}
                    isSaving={savingDialpadSafeguard}
                    onSave={handleSaveDialpadSafeguard}
                  />
                )}

                {/* Invite User Tab */}
                {activeSection === 'invite-email' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold text-theme-text dark:text-dark-theme-text mb-2">
                        Invite User via Email
                      </h3>
                      <p className="text-sm text-theme-text-secondary dark:text-dark-theme-text-secondary">
                        Send an invitation email with a temporary password. The user will receive login instructions.
                      </p>
                    </div>

                    <div className="bg-theme-bg dark:bg-dark-theme-bg rounded-lg p-6 border border-theme-border dark:border-dark-theme-border">
                      <form onSubmit={handleInviteUser} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-theme-text dark:text-dark-theme-text mb-2">
                            Email Address *
                          </label>
                          <input
                            type="email"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            required
                            className="w-full px-4 py-2 border border-theme-border dark:border-dark-theme-border rounded-lg bg-white dark:bg-gray-800 text-theme-text dark:text-dark-theme-text focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:outline-none"
                            placeholder="user@example.com"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-theme-text dark:text-dark-theme-text mb-2">
                              First Name
                            </label>
                            <input
                              type="text"
                              value={inviteFirstName}
                              onChange={(e) => setInviteFirstName(e.target.value)}
                              className="w-full px-4 py-2 border border-theme-border dark:border-dark-theme-border rounded-lg bg-white dark:bg-gray-800 text-theme-text dark:text-dark-theme-text focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:outline-none"
                              placeholder="John"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-theme-text dark:text-dark-theme-text mb-2">
                              Last Name
                            </label>
                            <input
                              type="text"
                              value={inviteLastName}
                              onChange={(e) => setInviteLastName(e.target.value)}
                              className="w-full px-4 py-2 border border-theme-border dark:border-dark-theme-border rounded-lg bg-white dark:bg-gray-800 text-theme-text dark:text-dark-theme-text focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:outline-none"
                              placeholder="Doe"
                            />
                          </div>
                        </div>
                        <button
                          type="submit"
                          disabled={inviting}
                          className="w-full px-6 py-3 bg-primary-light dark:bg-primary-dark text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                        >
                          {inviting ? 'Sending Invitation...' : 'Send Invitation'}
                        </button>
                      </form>
                    </div>
                  </div>
                )}

                {/* Create User Tab */}
                {activeSection === 'create-user' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold text-theme-text dark:text-dark-theme-text mb-2">
                        Create User with Temporary Password
                      </h3>
                      <p className="text-sm text-theme-text-secondary dark:text-dark-theme-text-secondary">
                        Create a user account and set a temporary password. You'll need to share the password with the user.
                      </p>
                    </div>

                    <div className="bg-theme-bg dark:bg-dark-theme-bg rounded-lg p-6 border border-theme-border dark:border-dark-theme-border">
                      <form onSubmit={handleCreateUser} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-theme-text dark:text-dark-theme-text mb-2">
                            Email Address *
                          </label>
                          <input
                            type="email"
                            value={createEmail}
                            onChange={(e) => setCreateEmail(e.target.value)}
                            required
                            className="w-full px-4 py-2 border border-theme-border dark:border-dark-theme-border rounded-lg bg-white dark:bg-gray-800 text-theme-text dark:text-dark-theme-text focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:outline-none"
                            placeholder="user@example.com"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-theme-text dark:text-dark-theme-text mb-2">
                              First Name
                            </label>
                            <input
                              type="text"
                              value={createFirstName}
                              onChange={(e) => setCreateFirstName(e.target.value)}
                              className="w-full px-4 py-2 border border-theme-border dark:border-dark-theme-border rounded-lg bg-white dark:bg-gray-800 text-theme-text dark:text-dark-theme-text focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:outline-none"
                              placeholder="John"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-theme-text dark:text-dark-theme-text mb-2">
                              Last Name
                            </label>
                            <input
                              type="text"
                              value={createLastName}
                              onChange={(e) => setCreateLastName(e.target.value)}
                              className="w-full px-4 py-2 border border-theme-border dark:border-dark-theme-border rounded-lg bg-white dark:bg-gray-800 text-theme-text dark:text-dark-theme-text focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:outline-none"
                              placeholder="Doe"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-theme-text dark:text-dark-theme-text mb-2">
                            Temporary Password *
                          </label>
                          <div className="flex space-x-2">
                            <input
                              type="text"
                              value={tempPassword}
                              onChange={(e) => setTempPassword(e.target.value)}
                              required
                              className="flex-1 px-4 py-2 border border-theme-border dark:border-dark-theme-border rounded-lg bg-white dark:bg-gray-800 text-theme-text dark:text-dark-theme-text focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:outline-none font-mono"
                              placeholder="Enter or generate password"
                            />
                            <button
                              type="button"
                              onClick={generateRandomPassword}
                              className="px-4 py-2 bg-theme-bg-secondary dark:bg-dark-theme-bg-secondary border border-theme-border dark:border-dark-theme-border rounded-lg hover:bg-theme-bg-tertiary dark:hover:bg-dark-theme-bg-tertiary transition-colors text-theme-text dark:text-dark-theme-text"
                            >
                              Generate
                            </button>
                          </div>
                        </div>
                        <button
                          type="submit"
                          disabled={creating}
                          className="w-full px-6 py-3 bg-primary-light dark:bg-primary-dark text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                        >
                          {creating ? 'Creating User...' : 'Create User'}
                        </button>
                      </form>
                    </div>
                  </div>
                )}

                {/* Create Organization Tab */}
                {activeSection === 'create-org' && userInfo?.user?.is_superuser && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold text-theme-text dark:text-dark-theme-text mb-2">
                        Create New Organization
                      </h3>
                      <p className="text-sm text-theme-text-secondary dark:text-dark-theme-text-secondary">
                        Create a new organization with an auto-generated unique code.
                      </p>
                    </div>

                    <div className="bg-theme-bg dark:bg-dark-theme-bg rounded-lg p-6 border border-theme-border dark:border-dark-theme-border">
                      <form onSubmit={handleCreateOrganization} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-theme-text dark:text-dark-theme-text mb-2">
                            Organization Name *
                          </label>
                          <input
                            type="text"
                            value={newOrgName}
                            onChange={(e) => setNewOrgName(e.target.value)}
                            required
                            className="w-full px-4 py-2 border border-theme-border dark:border-dark-theme-border rounded-lg bg-white dark:bg-gray-800 text-theme-text dark:text-dark-theme-text focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:outline-none"
                            placeholder="Acme Corporation"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-theme-text dark:text-dark-theme-text mb-2">
                            Description
                          </label>
                          <textarea
                            value={newOrgDescription}
                            onChange={(e) => setNewOrgDescription(e.target.value)}
                            rows={3}
                            className="w-full px-4 py-2 border border-theme-border dark:border-dark-theme-border rounded-lg bg-white dark:bg-gray-800 text-theme-text dark:text-dark-theme-text focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:outline-none"
                            placeholder="Optional description"
                          />
                        </div>
                        <button
                          type="submit"
                          disabled={creatingOrg}
                          className="w-full px-6 py-3 bg-primary-light dark:bg-primary-dark text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                        >
                          {creatingOrg ? 'Creating Organization...' : 'Create Organization'}
                        </button>
                      </form>

                      {/* Show generated code after creation */}
                      {newOrgCode && (
                        <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                          <h4 className="font-semibold text-green-800 dark:text-green-300 mb-2">
                            Organization Created!
                          </h4>
                          <p className="text-sm text-green-700 dark:text-green-400 mb-3">
                            Share this code with users to join:
                          </p>
                          <div className="flex items-center space-x-2">
                            <code className="flex-1 p-3 bg-white dark:bg-gray-800 border border-green-300 dark:border-green-700 rounded-lg text-xl font-mono font-bold text-green-700 dark:text-green-300">
                              {newOrgCode}
                            </code>
                            <button
                              onClick={() => copyToClipboard(newOrgCode)}
                              className="p-3 bg-green-600 dark:bg-green-700 text-white rounded-lg hover:opacity-90 transition-opacity"
                            >
                              {copied ? <CheckIcon className="w-5 h-5" /> : <ClipboardDocumentIcon className="w-5 h-5" />}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Switch Organization Tab */}
                {activeSection === 'switch-org' && userInfo?.user?.is_superuser && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold text-theme-text dark:text-dark-theme-text mb-2">
                        Switch Organization
                      </h3>
                      <p className="text-sm text-theme-text-secondary dark:text-dark-theme-text-secondary">
                        Change your active organization. This will reload the application.
                      </p>
                    </div>

                    <div className="bg-theme-bg dark:bg-dark-theme-bg rounded-lg p-6 border border-theme-border dark:border-dark-theme-border">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-theme-text dark:text-dark-theme-text mb-2">
                            Current Organization
                          </label>
                          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                            <p className="font-semibold text-blue-800 dark:text-blue-300">
                              {organization?.name}
                            </p>
                            <p className="text-sm text-blue-600 dark:text-blue-400">
                              Code: {organization?.organization_code}
                            </p>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-theme-text dark:text-dark-theme-text mb-2">
                            Switch To *
                          </label>
                          <select
                            value={selectedOrgId || ''}
                            onChange={(e) => setSelectedOrgId(Number(e.target.value))}
                            className="w-full px-4 py-2 border border-theme-border dark:border-dark-theme-border rounded-lg bg-white dark:bg-gray-800 text-theme-text dark:text-dark-theme-text focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:outline-none"
                          >
                            <option value="">Select an organization...</option>
                            {allOrganizations.map((org) => (
                              <option 
                                key={org.id} 
                                value={org.id}
                                disabled={org.id === organization?.id}
                              >
                                {org.name} ({org.organization_code})
                                {org.id === organization?.id ? ' - Current' : ''}
                              </option>
                            ))}
                          </select>
                        </div>

                        <button
                          onClick={handleSwitchOrganization}
                          disabled={switching || !selectedOrgId}
                          className="w-full px-6 py-3 bg-primary-light dark:bg-primary-dark text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                        >
                          {switching ? 'Switching Organization...' : 'Switch Organization'}
                        </button>

                        <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                          <p className="text-xs text-yellow-700 dark:text-yellow-300">
                            ⚠️ Switching organizations will reload the application and update all your data contexts.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default OrganizationSettingsModal;
